import type { ReportTemplate } from 'api/analytics-reports/types';
import {
  DEFAULT_QUICK_REPORT_TEMPLATE_CODE,
  type QuickReportScope,
} from 'app/modules/analytics/modules/reports/constants/quickReportDefaults';
import {
  buildProfileCardsForCreateReport,
  createEmptyProfileCard,
  type ReportProfileEditorState,
} from 'app/modules/analytics/modules/reports/types/ReportProfileEditorState';
import type { PageData, ProfileGroup } from 'types/Page';

export function resolveDefaultQuickReportTemplate(
  templates: ReportTemplate[],
): ReportTemplate | null {
  return (
    templates.find(
      (template) => template.code === DEFAULT_QUICK_REPORT_TEMPLATE_CODE,
    ) ??
    templates[0] ??
    null
  );
}

export function buildTemplatePrefillProfileCards(
  template: ReportTemplate,
  isGroupSelected: boolean,
  selectedProfileGroup: ProfileGroup | null,
  selectedPage: PageData | null,
): ReportProfileEditorState[] {
  return buildProfileCardsForCreateReport(
    isGroupSelected,
    selectedProfileGroup,
    selectedPage,
    template,
  );
}

function applyPerformanceMetricsToCards(
  cards: ReportProfileEditorState[],
  performanceMetrics: string[],
): ReportProfileEditorState[] {
  return cards.map((card) => ({
    ...card,
    performanceMetrics: [...performanceMetrics],
  }));
}

function stripContentPostsFromCards(
  cards: ReportProfileEditorState[],
): ReportProfileEditorState[] {
  return cards.map((card) => ({
    ...card,
    contentNote: '',
    contentDefaults: {
      content_order_by_posts: null,
      content_order_by_reels: null,
      content_post_count: null,
    },
  }));
}

export function buildOverviewQuickReportProfileCards(
  isGroupSelected: boolean,
  selectedProfileGroup: ProfileGroup | null,
  selectedPage: PageData | null,
): ReportProfileEditorState[] {
  // TODO(overview-pdf-be): Tieto karty + sections: [{ kind: 'overview' }] použiť v create
  // payload po doručení BE. @see overviewPdfExportBackendGaps.ts
  if (isGroupSelected) {
    return [createEmptyProfileCard('general', 0)];
  }

  if (selectedPage) {
    return [
      createEmptyProfileCard('profile', 0, {
        id: selectedPage.id,
        name: selectedPage.name ?? '',
        logoSrc: selectedPage.logo?.src,
        type: selectedPage.type,
      }),
    ];
  }

  return [];
}

export function buildQuickReportProfileCards(
  scope: QuickReportScope,
  template: ReportTemplate,
  isGroupSelected: boolean,
  selectedProfileGroup: ProfileGroup | null,
  selectedPage: PageData | null,
  allPerformanceMetrics: string[] = [],
): ReportProfileEditorState[] {
  if (scope === 'overview') {
    return buildOverviewQuickReportProfileCards(
      isGroupSelected,
      selectedProfileGroup,
      selectedPage,
    );
  }

  const cards = buildProfileCardsForCreateReport(
    isGroupSelected,
    selectedProfileGroup,
    selectedPage,
    template,
  );

  if (scope === 'full') {
    return cards;
  }

  const cardsWithPerformanceMetrics =
    allPerformanceMetrics.length > 0
      ? applyPerformanceMetricsToCards(cards, allPerformanceMetrics)
      : cards;

  return stripContentPostsFromCards(cardsWithPerformanceMetrics);
}
